<template>
  <section class="pb-2">
    <b-card
      class="my-50 client-table__data--presets"
      no-body
    >
      <b-skeleton-table
        v-if="isLoading"
        :rows="10"
        :columns="3"
        :table-props="{ bordered: true, striped: true }"
      />
      <b-table-simple
        v-else
        class="mb-0"
        fixed
      >
        <b-tbody>
          <b-tr class="preset-table_head">
            <b-td>
              {{ $tk('ClientPortal.MasterTable.Presets') }}
            </b-td>
            <b-td>
              {{ $tk('ClientPortal.MasterTable.AvailableColumns') }}
            </b-td>
            <b-td>
              {{ $tk('ClientPortal.MasterTable.ColumnOrder') }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-td class="border-right vertical-align-top px-2">
              <div
                class="preset-column"
              >
                <b-button
                  v-for="(defaultPreset,index) in wrapper.presets"
                  :key="index"
                  :class="getPresetButtonClass(defaultPreset)"
                  variant="outline-secondary"
                  size="sm"
                  class="outline-light-secondary"
                  block
                  @click="setPreset(defaultPreset)"
                >
                  {{ defaultPreset.name }}
                </b-button>
                <b-button
                  v-if="showSaveSelectedPresetButton()"
                  variant="outline-secondary"
                  size="sm"
                  class="my-2 active-preset outline-light-secondary"
                  block
                  :disabled="!preset.columns.length || isSaving"
                  @click="saveSelectedPreset"
                >
                  <GjIcon
                    name="Save"
                    class="mr-50"
                  />
                  {{ $tk('ClientPortal.MasterTable.SaveSelected') }}
                </b-button>
                <div id="savePresetContainer">
                  <b-button
                    v-if="showSaveAsNewPresetButton()"
                    id="edit-preset-modal"
                    v-b-modal.modal-save-new-preset
                    :disabled="!preset.columns.length"
                    variant="outline-secondary"
                    size="sm"
                    class="my-2 active-preset outline-light-secondary"
                    block
                  >
                    <GjIcon
                      name="Save"
                      class="mr-50"
                    />
                    {{ $tk('ClientPortal.MasterTable.SaveAsNew') }}
                  </b-button>
                  <b-modal
                    id="modal-save-new-preset"
                    :ok-title="$tk('ClientPortal.Form.Actions.Save')"
                    :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
                    centered
                    size="md"
                    :title="$tk('ClientPortal.MasterTable.NameOfPreset')"
                    no-enforce-focus
                    cancel-variant="outline-secondary"
                    @change="resetNewPresetName"
                    @ok="saveAsNewPreset"
                  >
                    <b-form-group
                      :label="$tk('ClientPortal.MasterTable.NameOfPreset')"
                      label-size="xs"
                      class="mb-0"
                    >
                      <b-form-input
                        v-model="newPresetName"
                        size="sm"
                        type="text"
                        min="1"
                        max="20"
                      />
                    </b-form-group>
                  </b-modal>
                </div>
              </div>
            </b-td>
            <b-td class="border-right vertical-align-top pl-2 pr-25">
              <div class="preset-column">
                <template v-for="column in wrapper.columns">
                  <b-form-checkbox
                    v-if="column.property != 'name'"
                    :key="column.id"
                    class="mb-1"
                    :checked="isColumnChecked(column.property)"
                    @change="columnChecked(column.property)"
                  >
                    {{ column.name }}
                  </b-form-checkbox>
                </template>
              </div>
            </b-td>
            <b-td class="vertical-align-top pl-2 pr-0">
              <draggable
                v-model="preset.columns"
                class="list-group preset-column pr-2"
                v-bind="dragOptions"
                @start="drag = true"
                @end="drag = false; hasPresetChanges = true"
              >
                <transition-group
                  type="transition"
                  :name="!drag ? 'flip-list' : null"
                >
                  <div
                    v-for="columnKey in preset.columns"
                    :key="columnKey"
                    class="order-columns form-check form-check-flat form-check-primary mb-50 mt-0 list-group-item"
                  >
                    <label class="form-check-label ml-0 eclipsis">
                      <GjIcon
                        name="Drag"
                        class="mr-2"
                      />
                      {{ wrapper.columns[columnKey].name }}
                    </label>
                  </div>
                </transition-group>
              </draggable>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </b-card>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import draggable from 'vuedraggable'
import { onMounted, computed, ref } from '@vue/composition-api'
import PresetService from '@/api/admin/preset.service'
import notification from '@/mixins/toastification'
import store from '@/store'

export default {
  components: {
    GjIcon,
    draggable,
  },
  mixins: [notification],
  setup() {
    const isLoading = ref(false)
    const isSaving = ref(false)
    const drag = ref(false)
    const hasPresetChanges = ref(false)
    const newPresetName = ref('')
    const dragOptions = computed(() => ({
      animation: 200,
      disabled: false,
      ghostClass: 'ghost',
    }))

    const preset = ref({})

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getPresets')
      preset.value = store.getters.getDefaultPreset
      isLoading.value = false
    })

    const wrapper = computed(() => store.getters.getPresets)

    const setPreset = async obj => {
      await store.dispatch('getPresets')
      preset.value = obj
      hasPresetChanges.value = true
    }

    const getPresetButtonClass = obj => {
      if (preset.value.id === obj.id) { return 'btn-primary' }
      return 'btn-light'
    }
    const showSaveSelectedPresetButton = () => hasPresetChanges.value

    const showSaveAsNewPresetButton = () => hasPresetChanges.value

    const isColumnChecked = column => preset.value.columns.indexOf(column) >= 0

    const columnChecked = column => {
      const { columns } = preset.value
      const index = columns.indexOf(column)
      if (index >= 0) {
        columns.splice(index, 1)
      } else {
        columns.push(column)
      }
      hasPresetChanges.value = true
    }

    const resetNewPresetName = () => {
      newPresetName.value = ''
    }

    const saveSelectedPreset = async () => {
      isSaving.value = true
      try {
        const response = await PresetService.updatePreset(preset.value)
        if (response.updated) {
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        hasPresetChanges.value = false
        isSaving.value = false
      }
    }
    const saveAsNewPreset = async () => {
      if (!newPresetName.value) return
      const newPreset = JSON.parse(JSON.stringify(preset.value))
      newPreset.name = newPresetName.value
      try {
        const response = await PresetService.createPreset(newPreset)
        if (response.inserted) {
          notification.methods.success(response.message)
          newPreset.id = response.id
          preset.value = newPreset
          wrapper.value.presets.push(preset.value)
          hasPresetChanges.value = false
        } else {
          notification.methods.error(response.message)
        }
      } catch (err) {
        notification.methods.error(err.message)
        await store.dispatch('getPresets')
      } finally {
        await store.dispatch('getPresets')
      }
    }

    return {
      drag,
      isLoading,
      dragOptions,
      hasPresetChanges,
      wrapper,
      preset,
      newPresetName,
      isSaving,
      setPreset,
      getPresetButtonClass,
      showSaveSelectedPresetButton,
      showSaveAsNewPresetButton,
      isColumnChecked,
      columnChecked,
      saveSelectedPreset,
      saveAsNewPreset,
      resetNewPresetName,
    }
  },

}
</script>

<style lang="scss">
@import '@/assets/scss/views/admin/configuration/table-presets.scss';
</style>
